import { useState, useEffect, useCallback } from "react";
import GameLayout from "../components/GameLayout";
import API from "../lib/api";
import { toast } from "sonner";
import { Users, Zap, Heart, TrendingUp, Wrench, ChevronDown, ChevronUp, Pickaxe, Footprints, Box, ShoppingBag, AlertTriangle, Coins, Utensils } from "lucide-react";
import GameSounds from "../lib/sounds";
import Paginator from "../components/Paginator";

const SLOT_ICONS = { tool: Pickaxe, shoes: Footprints, machine: Box, bag: ShoppingBag };
const SLOT_DESCRIPTIONS = {
  tool: "Increases coins/dig + unlocks resources",
  shoes: "Increases digs per day",
  machine: "Increases total earnings + unlocks tiers",
  bag: "Reduces daily food cost",
};

function BarDisplay({ icon: Icon, label, value, max, color, warning }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const isLow = pct < 50;
  return (
    <div className={`bg-[#0B0E14] rounded-xl p-3 ${isLow ? "border border-[#EF4444]/20" : ""}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color: isLow ? "#EF4444" : color }} />
          <span className="text-[10px] text-[#64748B] uppercase tracking-wider">{label}</span>
        </div>
        {isLow && <AlertTriangle className="w-3 h-3 text-[#EF4444] animate-pulse" />}
      </div>
      <div className="h-2 bg-[#1C212E] rounded-full overflow-hidden mb-1.5">
        <div className="h-full rounded-full transition-all duration-500" style={{
          width: `${pct}%`,
          backgroundColor: isLow ? "#EF4444" : color,
        }} />
      </div>
      <div className="flex items-center justify-between">
        <span className="font-mono-game text-sm font-bold" style={{ color: isLow ? "#EF4444" : "#F8FAFC" }}>
          {typeof value === "number" && value < 1 ? `${(value * 100).toFixed(0)}%` : `${value}/${max}`}
        </span>
        {isLow && <span className="text-[9px] text-[#EF4444] font-bold">{warning}</span>}
      </div>
    </div>
  );
}

export default function WorkersPage() {
  const [workers, setWorkers] = useState([]);
  const [equipment, setEquipment] = useState({});
  const [storage, setStorage] = useState(null);
  const [foodCostPerDay, setFoodCostPerDay] = useState(10);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState("");
  const [filterTier, setFilterTier] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("default");
  const [page, setPage] = useState(1);
  const PER_PAGE = 12;

  const load = useCallback(async () => {
    try {
      const [wRes, eRes, sRes] = await Promise.all([API.get("/workers"), API.get("/equipment"), API.get("/user-storage")]);
      setWorkers(wRes.data.workers);
      setEquipment(eRes.data.equipment);
      setStorage(sRes.data);
      if (wRes.data.food_cost_per_day) setFoodCostPerDay(wRes.data.food_cost_per_day);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const upgrade = async (workerId, stat) => {
    setLoading(`${workerId}-${stat}`);
    try {
      const { data } = await API.post("/workers/upgrade", { stat, worker_id: workerId });
      toast.success(data.message);
      GameSounds.upgrade();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const equip = async (workerId, slot, itemName) => {
    setLoading(`equip-${workerId}-${slot}`);
    try {
      const { data } = await API.post("/workers/equip", { slot, item_name: itemName, worker_id: workerId });
      toast.success(data.message);
      GameSounds.purchase();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const refill = async (workerId) => {
    setLoading(`refill-${workerId}`);
    try {
      const { data } = await API.post("/workers/rest", { worker_id: workerId });
      toast.success(data.message);
      GameSounds.reward();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  // Helpers
  const getEquipTier = (w, slot) => {
    const itemName = w.equipment?.[slot] || "";
    const items = equipment[slot] || [];
    const idx = items.findIndex(i => i.name === itemName);
    return idx >= 0 ? idx + 1 : 1;
  };

  const getUnlockedResources = (w) => {
    const unlocked = ["Coal", "Iron"];
    if (getEquipTier(w, "tool") >= 2) unlocked.push("Silver");
    if (getEquipTier(w, "tool") >= 3) unlocked.push("Gold");
    if (getEquipTier(w, "machine") >= 2) unlocked.push("Diamond");
    if (getEquipTier(w, "machine") >= 3) unlocked.push("Mythril");
    return unlocked;
  };

  const getEquipBonus = (w) => {
    let total = 1;
    for (const slot of ["tool", "shoes", "machine", "bag"]) {
      const itemName = w.equipment?.[slot] || "";
      const items = equipment[slot] || [];
      const item = items.find(i => i.name === itemName);
      if (item) total *= item.bonus || 1;
    }
    return total;
  };

  const estimateDaily = (w) => {
    return w.daily_rate || 0;
  };

  const getRefillCost = (w) => {
    const energyNeeded = (w.max_energy || 100) - (w.energy || 0);
    const staminaNeeded = 100 - (w.hunger || 0);
    const effNeeded = Math.max(0, Math.round((1.0 - (w.efficiency || 1)) * 100));
    return energyNeeded + staminaNeeded + effNeeded;
  };

  const assign = async (workerId, type, value) => {
    setLoading(`assign-${workerId}-${type}`);
    try {
      const { data } = await API.post("/workers/assign-storage", { worker_id: workerId, type, value });
      toast.success(data.message);
      GameSounds.upgrade();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const deployed = workers.filter(w => w.status === "deployed");
  const idle = workers.filter(w => w.status !== "deployed");
  const RES_COLORS = { Coal: "#94A3B8", Iron: "#D1D5DB", Silver: "#E2E8F0", Gold: "#FCD34D", Diamond: "#93C5FD", Mythril: "#D8B4FE" };
  const TIER_COLORS = { bronze: { bg: "#CD7F3215", text: "#CD7F32" }, silver: { bg: "#C0C0C015", text: "#C0C0C0" }, gold: { bg: "#FFD60015", text: "#FFD600" } };

  return (
    <GameLayout title="Workers">
      <div className="space-y-6" data-testid="workers-page">
        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 text-center">
            <p className="font-mono-game text-2xl font-bold text-[#00F0FF]">{workers.length}</p>
            <p className="text-xs text-[#64748B] tracking-wider uppercase">Workers</p>
          </div>
          <div className="bg-[#131722] rounded-xl border border-[#10B981]/20 p-4 text-center">
            <p className="font-mono-game text-2xl font-bold text-[#10B981]">{deployed.length}</p>
            <p className="text-xs text-[#64748B] tracking-wider uppercase">Deployed</p>
          </div>
          <div className="bg-[#131722] rounded-xl border border-[#FFD600]/20 p-4 text-center">
            <p className="font-mono-game text-2xl font-bold text-[#FFD600]">~{workers.reduce((s, w) => s + estimateDaily(w), 0)}</p>
            <p className="text-xs text-[#64748B] tracking-wider uppercase">Est. Coins/Day</p>
          </div>
          <div className="bg-[#131722] rounded-xl border border-[#EF4444]/20 p-4 text-center">
            <p className="font-mono-game text-2xl font-bold text-[#EF4444]">{workers.reduce((s, w) => s + Math.max(1, Math.round(foodCostPerDay / (w.bag_bonus || 1))), 0)}</p>
            <p className="text-xs text-[#64748B] tracking-wider uppercase">Food Cost/Day</p>
          </div>
        </div>

        {/* Resource Storage */}
        {storage && (
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 overflow-hidden" data-testid="resource-storage">
            <div className="px-4 py-3 border-b border-[#2A3241]/30 flex items-center gap-2">
              <Box className="w-4 h-4 text-[#B026FF]" />
              <span className="text-sm font-bold text-[#F8FAFC] uppercase tracking-wider">Resource Storage</span>
              <span className="text-[10px] text-[#64748B] ml-auto">From shop purchases — assign to workers later</span>
            </div>
            <div className="p-4">
              {/* Stat Resources */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                <div className="bg-[#0B0E14] rounded-lg p-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-[#00F0FF]" />
                  </div>
                  <div>
                    <p className="font-mono text-lg font-bold text-[#F8FAFC]">{storage.energy || 0}</p>
                    <p className="text-[10px] text-[#64748B] uppercase">Energy</p>
                  </div>
                </div>
                <div className="bg-[#0B0E14] rounded-lg p-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-[#10B981]" />
                  </div>
                  <div>
                    <p className="font-mono text-lg font-bold text-[#F8FAFC]">{storage.efficiency || 0}</p>
                    <p className="text-[10px] text-[#64748B] uppercase">Efficiency</p>
                  </div>
                </div>
              </div>
              {/* Equipment Resources */}
              {(storage.tools?.length > 0 || storage.shoes?.length > 0 || storage.machines?.length > 0 || storage.bags?.length > 0) ? (
                <div className="space-y-2">
                  {[
                    { label: "Tools", items: storage.tools || [], icon: Pickaxe, color: "#F59E0B" },
                    { label: "Shoes", items: storage.shoes || [], icon: Footprints, color: "#3B82F6" },
                    { label: "Machines", items: storage.machines || [], icon: Box, color: "#B026FF" },
                    { label: "Bags", items: storage.bags || [], icon: ShoppingBag, color: "#10B981" },
                  ].filter(g => g.items.length > 0).map(({ label, items, icon: Icon, color }) => {
                    const counts = {};
                    items.forEach(n => { counts[n] = (counts[n] || 0) + 1; });
                    return (
                      <div key={label} className="bg-[#0B0E14] rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="w-3.5 h-3.5" style={{ color }} />
                          <span className="text-[10px] text-[#64748B] uppercase tracking-wider font-bold">{label}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(counts).map(([name, count]) => (
                            <span key={name} className="text-xs px-2.5 py-1 rounded-lg border font-mono" style={{ borderColor: `${color}30`, color, backgroundColor: `${color}08` }}>
                              {name} {count > 1 ? `x${count}` : ""}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                storage.energy === 0 && storage.efficiency === 0 && (
                  <p className="text-xs text-[#475569] text-center py-2">No resources in storage. Buy packs from the Shop!</p>
                )
              )}
            </div>
          </div>
        )}

        {workers.length === 0 ? (
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-12 text-center">
            <Users className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
            <p className="text-[#94A3B8]">No workers. Visit the Shop to get started!</p>
          </div>
        ) : (() => {
          const tierOptions = [...new Set(workers.map(w => w.tier).filter(Boolean))];
          let filtered = workers;
          if (filterTier !== "all") filtered = filtered.filter(w => w.tier === filterTier);
          if (filterStatus === "deployed") filtered = filtered.filter(w => w.status === "deployed");
          else if (filterStatus === "idle") filtered = filtered.filter(w => w.status !== "deployed");
          else if (filterStatus === "low") filtered = filtered.filter(w => w.energy < 50 || w.hunger < 50);
          if (sortBy === "energy") filtered = [...filtered].sort((a, b) => (a.energy || 0) - (b.energy || 0));
          else if (sortBy === "stamina") filtered = [...filtered].sort((a, b) => (a.hunger || 0) - (b.hunger || 0));
          else if (sortBy === "rate") filtered = [...filtered].sort((a, b) => (b.daily_rate || 0) - (a.daily_rate || 0));
          const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
          const safePage = Math.min(page, totalPages);
          const pageItems = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);
          return (
          <div className="space-y-3">
            <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-3 flex items-center gap-2 flex-wrap">
              <select value={filterTier} onChange={(e) => { setFilterTier(e.target.value); setPage(1); }} data-testid="workers-filter-tier"
                className="bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 py-1 text-xs">
                <option value="all">All tiers</option>
                {tierOptions.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }} data-testid="workers-filter-status"
                className="bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 py-1 text-xs">
                <option value="all">All status</option>
                <option value="deployed">Deployed</option>
                <option value="idle">Idle</option>
                <option value="low">Low energy/stamina</option>
              </select>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="workers-sort"
                className="bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 py-1 text-xs">
                <option value="default">Default order</option>
                <option value="energy">Lowest energy first</option>
                <option value="stamina">Lowest stamina first</option>
                <option value="rate">Highest rate first</option>
              </select>
              <span className="text-[10px] text-[#64748B] ml-auto">{filtered.length} of {workers.length} shown</span>
            </div>
            {pageItems.map((w) => {
              const isExpanded = expanded === w.worker_id;
              const isDeployed = w.status === "deployed";
              const refillCost = getRefillCost(w);
              const dailyEst = estimateDaily(w);
              const unlocked = getUnlockedResources(w);
              const energyLow = w.energy < 50;
              const staminaLow = w.hunger < 50;
              const effLow = (w.efficiency || 1) < 0.5;

              return (
                <div key={w.worker_id} className={`bg-[#131722] rounded-2xl border overflow-hidden transition-all ${isDeployed ? "border-[#10B981]/30" : (energyLow || staminaLow) ? "border-[#EF4444]/20" : "border-[#2A3241]/40"}`} data-testid={`worker-card-${w.worker_id}`}>
                  {/* Header */}
                  <div className="p-4 flex items-center gap-4 cursor-pointer hover:bg-[#1C212E]/30 transition-colors" onClick={() => setExpanded(isExpanded ? null : w.worker_id)}>
                    <div className="relative">
                      <img src="https://static.prod-images.emergentagent.com/jobs/bf9e0d20-757d-4e16-8aee-b054e0c78697/images/228575951fa70724d1d3d45f298bac77e6610d2d40e47e326da6737b86d6aca0.png"
                        alt={w.name} className="w-14 h-14 rounded-xl object-cover border border-[#2A3241]" />
                      <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-[#131722] ${isDeployed ? "bg-[#10B981]" : energyLow ? "bg-[#EF4444] animate-pulse" : "bg-[#64748B]"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-bold text-[#F8FAFC]">{w.name}</h3>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono-game ${isDeployed ? "bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30" : "bg-[#1C212E] text-[#64748B] border border-[#2A3241]"}`}>
                          {isDeployed ? "DEPLOYED" : "IDLE"}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-mono-game uppercase" style={{ backgroundColor: TIER_COLORS[w.tier]?.bg || "#94A3B815", color: TIER_COLORS[w.tier]?.text || "#94A3B8", border: `1px solid ${TIER_COLORS[w.tier]?.text || "#94A3B8"}30` }}>
                          {w.tier || "gold"}
                        </span>
                        {(energyLow || staminaLow) && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30 font-mono-game">LOW BARS</span>}
                        {w.days_remaining != null && w.days_remaining <= 30 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30 font-mono-game">{w.days_remaining}d LEFT</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-[#FFD600] font-mono-game">~{dailyEst}c/day</span>
                        <span className="text-xs text-[#64748B]">{unlocked.length} resources</span>
                      </div>
                    </div>
                    {/* Mini bars */}
                    <div className="hidden sm:flex items-center gap-2">
                      {[
                        { val: w.energy, max: 100, color: w.energy < 50 ? "#EF4444" : "#00F0FF", label: "E" },
                        { val: w.hunger, max: 100, color: w.hunger < 50 ? "#EF4444" : "#FFD600", label: "S" },
                        { val: Math.round((w.efficiency || 1) * 100), max: 100, color: (w.efficiency || 1) < 0.5 ? "#EF4444" : "#10B981", label: "%" },
                      ].map((s, i) => (
                        <div key={i} className="w-14 text-center">
                          <div className="h-1.5 bg-[#0B0E14] rounded-full overflow-hidden mb-0.5">
                            <div className="h-full rounded-full transition-all" style={{ width: `${(s.val / s.max) * 100}%`, backgroundColor: s.color }} />
                          </div>
                          <span className="text-[8px] font-mono-game" style={{ color: s.color }}>{s.label}:{s.val}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      {!isDeployed && refillCost > 0 && (
                        <button onClick={(e) => { e.stopPropagation(); refill(w.worker_id); }} disabled={!!loading} data-testid={`refill-${w.worker_id}`}
                          className="px-3 py-1.5 text-[10px] font-bold uppercase bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 rounded-lg hover:bg-[#00F0FF]/20 disabled:opacity-50">
                          <Coins className="w-3 h-3 inline mr-0.5" />{refillCost}c Refill
                        </button>
                      )}
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-[#64748B]" /> : <ChevronDown className="w-4 h-4 text-[#64748B]" />}
                    </div>
                  </div>

                  {/* Expanded */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 border-t border-[#2A3241]/30 animate-slide-up">
                      {/* Bars */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                        <BarDisplay icon={Zap} label="Energy" value={w.energy} max={100} color="#00F0FF" warning="DIGS -50%" />
                        <BarDisplay icon={Heart} label="Stamina" value={w.hunger} max={100} color="#FFD600" warning="COINS -50%" />
                        <BarDisplay icon={TrendingUp} label="Efficiency" value={Math.round((w.efficiency || 1) * 100)} max={100} color="#10B981" warning="LOW TIERS" />
                      </div>

                      {/* Bar info */}
                      <div className="mt-3 bg-[#0B0E14] rounded-lg p-3 border border-[#2A3241]/30">
                        <p className="text-[10px] font-mono-game text-[#64748B] tracking-wider uppercase mb-1">Daily Depletion</p>
                        <div className="flex flex-wrap gap-3 text-[10px]">
                          <span className="text-[#00F0FF]">Energy: -10/day</span>
                          <span className="text-[#FFD600]">Stamina: -8/day</span>
                          <span className="text-[#10B981]">Efficiency: -5/day</span>
                          <span className="text-[#EF4444]"><Utensils className="w-3 h-3 inline" /> Food: 10c/day</span>
                        </div>
                      </div>

                      {/* Production estimate */}
                      <div className="mt-3 bg-[#0B0E14] rounded-lg p-3 border border-[#FFD600]/20">
                        <p className="text-[10px] font-mono-game text-[#FFD600] tracking-wider uppercase mb-2">Production Estimate</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div className="text-center">
                            <p className="font-mono-game text-lg font-bold text-[#FFD600]">~{dailyEst}</p>
                            <p className="text-[9px] text-[#64748B]">coins/day</p>
                          </div>
                          <div className="text-center">
                            <p className="font-mono-game text-lg font-bold text-[#00F0FF]">{getEquipBonus(w).toFixed(1)}x</p>
                            <p className="text-[9px] text-[#64748B]">equip bonus</p>
                          </div>
                          <div className="text-center">
                            {(() => { const fc = Math.max(1, Math.round(foodCostPerDay / (w.bag_bonus || 1))); return <p className="font-mono-game text-lg font-bold text-[#EF4444]">-{fc}c</p>; })()}
                            <p className="text-[9px] text-[#64748B]">food/day</p>
                          </div>
                          <div className="text-center">
                            {(() => { const fc = Math.max(1, Math.round(foodCostPerDay / (w.bag_bonus || 1))); const net = dailyEst - fc; return <p className="font-mono-game text-lg font-bold" style={{ color: net > 0 ? "#10B981" : "#EF4444" }}>~{net}</p>; })()}
                            <p className="text-[9px] text-[#64748B]">net/day</p>
                          </div>
                        </div>
                      </div>

                      {/* Unlocked Resources */}
                      <div className="mt-3">
                        <p className="text-[10px] font-mono-game text-[#64748B] tracking-wider uppercase mb-2">Unlocked Resources</p>
                        <div className="flex flex-wrap gap-1.5">
                          {["Coal", "Iron", "Silver", "Gold", "Diamond", "Mythril"].map(rn => {
                            const isUnlocked = unlocked.includes(rn);
                            return (
                              <span key={rn} className={`px-2 py-1 rounded-lg text-[10px] font-mono-game border ${isUnlocked ? "" : "opacity-30 line-through"}`}
                                style={{ backgroundColor: isUnlocked ? `${RES_COLORS[rn]}15` : "#1C212E", color: isUnlocked ? RES_COLORS[rn] : "#64748B", borderColor: isUnlocked ? `${RES_COLORS[rn]}30` : "#2A3241" }}>
                                {rn}
                              </span>
                            );
                          })}
                        </div>
                        <p className="text-[9px] text-[#64748B] mt-1">Tool Tier 2→Silver, Tier 3→Gold | Machine Tier 2→Diamond, Tier 3→Mythril</p>
                      </div>

                      {/* Equipment */}
                      <div className="mt-4">
                        <p className="text-xs font-mono-game text-[#64748B] tracking-[0.15em] uppercase mb-2">Equipment</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {["tool", "shoes", "machine", "bag"].map((slot) => {
                            const current = w.equipment?.[slot] || "None";
                            const items = equipment[slot] || [];
                            const SlotIcon = SLOT_ICONS[slot] || Wrench;
                            const tier = getEquipTier(w, slot);
                            return (
                              <div key={slot} className="bg-[#0B0E14] rounded-xl p-3">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-1.5">
                                    <SlotIcon className="w-3 h-3 text-[#FFD600]" />
                                    <span className="text-[10px] text-[#64748B] uppercase tracking-wider capitalize">{slot}</span>
                                  </div>
                                  <span className="text-[8px] font-mono-game text-[#00F0FF] bg-[#00F0FF]/10 px-1 rounded">T{tier}</span>
                                </div>
                                <p className="text-xs font-medium text-[#F8FAFC] mb-1 truncate">{current}</p>
                                <p className="text-[8px] text-[#64748B] mb-2">{SLOT_DESCRIPTIONS[slot]}</p>
                                <select value={current} onChange={(e) => equip(w.worker_id, slot, e.target.value)} disabled={!!loading || isDeployed}
                                  data-testid={`equip-${w.worker_id}-${slot}`}
                                  className="w-full h-7 bg-[#131722] border border-[#2A3241] text-[#94A3B8] rounded px-1 text-[10px] disabled:opacity-30">
                                  {items.map(item => (
                                    <option key={item.name} value={item.name}>{item.name} ({item.bonus}x · {item.cost}c)</option>
                                  ))}
                                </select>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Assign from Storage */}
                      {storage && (storage.energy > 0 || storage.efficiency > 0 || storage.tools?.length > 0 || storage.shoes?.length > 0 || storage.machines?.length > 0 || storage.bags?.length > 0) && (
                        <div className="mt-4" data-testid={`assign-storage-${w.worker_id}`}>
                          <p className="text-xs font-mono-game text-[#B026FF] tracking-[0.15em] uppercase mb-2">Assign from Storage</p>
                          <div className="bg-[#0B0E14] rounded-xl p-3 space-y-3 border border-[#B026FF]/20">
                            {/* Energy assign */}
                            {storage.energy > 0 && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <Zap className="w-3.5 h-3.5 text-[#00F0FF]" />
                                <span className="text-[10px] text-[#64748B] uppercase w-16">Energy</span>
                                <span className="text-xs text-[#94A3B8] font-mono">{storage.energy} avail</span>
                                {[10, 20, 50].filter(v => v <= storage.energy).map(amt => (
                                  <button key={amt} onClick={() => assign(w.worker_id, "energy", amt)}
                                    disabled={!!loading} data-testid={`assign-energy-${amt}-${w.worker_id}`}
                                    className="px-2 py-1 text-[10px] font-bold bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 rounded hover:bg-[#00F0FF]/20 disabled:opacity-30">
                                    +{amt}
                                  </button>
                                ))}
                                <button onClick={() => assign(w.worker_id, "energy", storage.energy)}
                                  disabled={!!loading} data-testid={`assign-energy-all-${w.worker_id}`}
                                  className="px-2 py-1 text-[10px] font-bold bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 rounded hover:bg-[#00F0FF]/20 disabled:opacity-30">
                                  All
                                </button>
                              </div>
                            )}
                            {/* Efficiency assign */}
                            {storage.efficiency > 0 && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <TrendingUp className="w-3.5 h-3.5 text-[#10B981]" />
                                <span className="text-[10px] text-[#64748B] uppercase w-16">Efficiency</span>
                                <span className="text-xs text-[#94A3B8] font-mono">{storage.efficiency} avail</span>
                                {[0.1, 0.5].filter(v => v <= storage.efficiency).map(amt => (
                                  <button key={amt} onClick={() => assign(w.worker_id, "efficiency", amt)}
                                    disabled={!!loading} data-testid={`assign-eff-${amt}-${w.worker_id}`}
                                    className="px-2 py-1 text-[10px] font-bold bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30 rounded hover:bg-[#10B981]/20 disabled:opacity-30">
                                    +{amt}
                                  </button>
                                ))}
                                <button onClick={() => assign(w.worker_id, "efficiency", storage.efficiency)}
                                  disabled={!!loading} data-testid={`assign-eff-all-${w.worker_id}`}
                                  className="px-2 py-1 text-[10px] font-bold bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30 rounded hover:bg-[#10B981]/20 disabled:opacity-30">
                                  All
                                </button>
                              </div>
                            )}
                            {/* Equipment assign */}
                            {[
                              { slot: "tool", label: "Tool", items: storage.tools || [], color: "#F59E0B", icon: Pickaxe },
                              { slot: "shoes", label: "Shoes", items: storage.shoes || [], color: "#3B82F6", icon: Footprints },
                              { slot: "machine", label: "Machine", items: storage.machines || [], color: "#B026FF", icon: Box },
                              { slot: "bag", label: "Bag", items: storage.bags || [], color: "#10B981", icon: ShoppingBag },
                            ].filter(g => g.items.length > 0).map(({ slot, label, items, color, icon: Icon }) => {
                              const unique = [...new Set(items)];
                              return (
                                <div key={slot} className="flex items-center gap-2 flex-wrap">
                                  <Icon className="w-3.5 h-3.5" style={{ color }} />
                                  <span className="text-[10px] text-[#64748B] uppercase w-16">{label}</span>
                                  {unique.map(name => (
                                    <button key={name} onClick={() => assign(w.worker_id, slot, name)}
                                      disabled={!!loading || isDeployed} data-testid={`assign-${slot}-${w.worker_id}`}
                                      className="px-2 py-1 text-[10px] font-bold rounded border hover:opacity-80 disabled:opacity-30"
                                      style={{ backgroundColor: `${color}10`, color, borderColor: `${color}30` }}>
                                      {name} {items.filter(i => i === name).length > 1 ? `x${items.filter(i => i === name).length}` : ""}
                                    </button>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <Paginator page={safePage} totalPages={totalPages} total={filtered.length} onPageChange={setPage} testIdPrefix="workers-pager" />
          </div>
          );
        })()}
      </div>
    </GameLayout>
  );
}
